import React, { useContext } from 'react';

import { Highlight } from '@/components/Highlight';
import { User } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import OffDutyChip from '@/core/users/common/OffDutyChip';
import InvitedChip from '@/core/users/UserPermissions/UserDetails/InvitedChip';
import theme from '@/theme';

type Props = {
  user: User;
}
export function UserDetailsName ({ user }: Props) {
  const { currentAccount, hasAccessTo } = useContext(AuthContext);
  const valueToDisplay = user.displayName || user.email;
  const { firstName, restOfName, isSeparatedBySpace } = split(valueToDisplay);

  const isDisplayingEmail = valueToDisplay === user.email;
  const invited = user.permissions.core?.state === 'invited' || user.permissions.uvk?.state === 'invited';

  return (
    <>
      {hasAccessTo('ticketing_system') && !invited && user.permissions?.core?.offDuty && (
        <div className="absolute -mt-6">
          <OffDutyChip />
        </div>
      )}
      {invited && (
        <div className="absolute -mt-6">
          <InvitedChip />
        </div>
      )}
      <div
        className="pr-8 font-serif font-bold text-blue-900 truncate"
        style={{ fontSize: (100 / Math.max(valueToDisplay.length, 8)) + 35 }}
        aria-hidden
      >
        <Highlight
          variant="macheteInverse"
          text={firstName}
          color={theme.palette.blue['300']}
        />
        <span
          className={`${isSeparatedBySpace ? 'ml-2' : ''}`}
          title={valueToDisplay}
        >
          {restOfName}
        </span>
      </div>
      <h1 className="sr-only">{ valueToDisplay }</h1>
      {(currentAccount?.coreAccess || !isDisplayingEmail) && (
        <div className="flex items-center mt-3 text-sm">
          {!isDisplayingEmail && (
            <>
              <span className="mr-1 font-bold">Email:</span>
              <span className="mr-3">{user.email}</span>
              {currentAccount?.coreAccess && (
                <span className="mr-3 bg-blue-900 rounded-full w-1.5 h-1.5" />
              )}
            </>
          )}
          {currentAccount?.coreAccess && (
            <a href={`/admin/v3/users/${user.id}`} className="text-blue-600" target="_blank" rel="noreferrer">
              View profile
            </a>
          )}
        </div>
      )}
    </>
  );
}

function split(str: string): {
  firstName: string,
  restOfName: string,
  isSeparatedBySpace: boolean
} {
  const split = str.split(' ');
  if (split.length > 1) {
    const [firstName, ...rest] = split;
    return {
      firstName,
      restOfName: rest.join(' '),
      isSeparatedBySpace: true,
    };
  }
  return {
    firstName: str.slice(0, 7),
    restOfName: str.slice(7, str.length),
    isSeparatedBySpace: false,
  };
}
